import { useMemo, useState } from 'react';

import { Disclosure } from './ui/Disclosure.js';
import type {
  CountryIncentiveRow,
  CountryRecommendation,
  SearchParametersState,
} from '../lib/api.js';

export interface SearchParametersDraft {
  countries: string[];
  city: string | null;
  radiusKm: number | null;
  shootWindowStart: string | null;
  shootWindowEnd: string | null;
  budgetBand: string | null;
  studioPreference: string | null;
  interiorExterior: string | null;
  twinRequired: boolean;
  constraintsNote: string | null;
}

const BUDGET_BANDS = [
  'Under €40k total',
  '€40–80k total',
  '€80–120k total',
  '€120–250k total',
  'Over €250k total',
];

/**
 * The constraints the screenplay cannot supply.
 *
 * A breakdown says a scene needs a hotel room. It cannot say the production
 * shoots in Bulgaria in October on €120k and wants practical interiors — and
 * those are the facts that decide whether a match is worth reading. Asking for
 * them here, once, is cheaper than a producer rejecting forty results to
 * discover we searched the wrong country.
 *
 * SKIP IS A REAL ANSWER, NOT AN ESCAPE. A producer who does not yet know their
 * territories is the normal case at this stage, and demanding an answer would
 * push them to invent one — which is worse than no constraint, because an
 * invented country silently excludes everything else. The skip is a button of
 * its own, and the server records that it was chosen.
 *
 * THE REBATE FIGURES ARE SOURCED, AND DATED. They come from the 42-scheme
 * dataset compiled in July 2026 and verified against national sources — not
 * from the city ranking's modelled scores, which must never drive a choice like
 * this. The disclaimer travels with them because incentive terms change without
 * notice and a producer budgeting against a stale rate has a real problem.
 */
export function SearchParametersForm({
  current,
  catalogue,
  recommended,
  disclaimer,
  busy = false,
  onSearch,
  onSkip,
  onCancel,
  framed = false,
}: {
  current: SearchParametersState;
  catalogue: readonly CountryIncentiveRow[];
  recommended: readonly CountryRecommendation[];
  disclaimer: string;
  busy?: boolean;
  onSearch: (draft: SearchParametersDraft) => void;
  onSkip: () => void;
  /** Close without saving — offered when the criteria were already answered once. */
  onCancel?: () => void;
  /** Opened from the criteria bar: the bar already says what this is, so no heading. */
  framed?: boolean;
}) {
  const [countries, setCountries] = useState<string[]>([...current.countries]);
  const [filter, setFilter] = useState('');
  const [city, setCity] = useState(current.city ?? '');
  const [radiusKm, setRadiusKm] = useState(current.radiusKm === null ? '' : String(current.radiusKm));
  const [from, setFrom] = useState(current.shootWindowStart ?? '');
  const [to, setTo] = useState(current.shootWindowEnd ?? '');
  const [budgetBand, setBudgetBand] = useState(current.budgetBand ?? '');
  const [studio, setStudio] = useState(current.studioPreference ?? 'BOTH');
  const [intExt, setIntExt] = useState(current.interiorExterior ?? 'BOTH');
  const [note, setNote] = useState(current.constraintsNote ?? '');
  const [twinRequired, setTwinRequired] = useState(current.twinRequired);

  const byIso = useMemo(() => new Map(catalogue.map((c) => [c.iso, c])), [catalogue]);

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const list = term === '' ? catalogue : catalogue.filter((c) => c.name.toLowerCase().includes(term));
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogue, filter]);

  const toggle = (iso: string): void => {
    setCountries((c) => (c.includes(iso) ? c.filter((x) => x !== iso) : [...c, iso]));
  };

  const draft = (): SearchParametersDraft => ({
    countries,
    city: city.trim() === '' ? null : city.trim(),
    // The 2 km floor mirrors the public search, where it exists to stop a tight
    // radius around one point being used to triangulate a listing's address.
    radiusKm: radiusKm.trim() === '' ? null : Math.max(2, Number(radiusKm)),
    shootWindowStart: from === '' ? null : from,
    shootWindowEnd: to === '' ? null : to,
    budgetBand: budgetBand === '' ? null : budgetBand,
    studioPreference: studio,
    interiorExterior: intExt,
    twinRequired,
    constraintsNote: note.trim() === '' ? null : note.trim(),
  });

  return (
    <section data-testid="search-parameters">
      {framed ? null : (
        <>
          <h2 className="dh" style={{ fontSize: 26 }}>
            Search criteria
          </h2>
          <p className="mm" style={{ fontSize: 13, margin: '6px 0 0' }}>
            What the script can’t tell us — where, when and on what budget. It decides which
            locations are worth reading.
          </p>
        </>
      )}

      <div className="split-3" style={{ marginTop: framed ? 0 : 20 }}>
        <div style={{ minWidth: 0 }}>
          <p className="flbl" style={{ margin: '0 0 8px' }}>
            Countries or regions{' '}
            <span className="mm" style={{ fontWeight: 400 }}>
              — pick as many as the production can shoot in
            </span>
          </p>

          <div
            style={{
              border: '1px solid var(--color-divider)',
              padding: 8,
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {countries.map((iso) => (
              <span
                key={iso}
                className="chip"
                data-testid={`country-chip-${iso}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {byIso.get(iso)?.name ?? iso}
                <button
                  type="button"
                  aria-label={`Remove ${byIso.get(iso)?.name ?? iso}`}
                  onClick={() => toggle(iso)}
                  style={{ background: 'none', border: 0, cursor: 'pointer', font: 'inherit' }}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              value={filter}
              disabled={busy}
              placeholder="Type to filter countries…"
              onChange={(e) => setFilter(e.target.value)}
              data-testid="country-filter"
              style={{ flex: 1, minWidth: 160, border: 0, font: 'inherit', padding: 4 }}
            />
          </div>

          <ul
            data-testid="country-list"
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              maxHeight: 190,
              overflowY: 'auto',
              border: '1px solid var(--color-divider)',
              borderTop: 0,
            }}
          >
            {visible.map((c) => (
              <li key={c.iso}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '7px 10px',
                    cursor: 'pointer',
                    flexWrap: 'wrap',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={countries.includes(c.iso)}
                    disabled={busy}
                    onChange={() => toggle(c.iso)}
                    data-testid={`country-${c.iso}`}
                  />
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</span>
                  {/* The label, not the number: "~38–40%" and "Up to 30%" say
                      something a single figure does not. */}
                  <span className="mm" style={{ marginLeft: 'auto', fontSize: 12 }}>
                    {c.label} {c.kind.toLowerCase()}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ minWidth: 0 }}>
          <p className="flbl" style={{ margin: '0 0 8px' }}>
            City + radius
          </p>
          <input
            value={city}
            disabled={busy}
            placeholder="e.g. Sofia"
            onChange={(e) => setCity(e.target.value)}
            data-testid="search-city"
            style={{ width: '100%', padding: '9px 10px', border: '1px solid var(--color-divider)', font: 'inherit' }}
          />
          <input
            value={radiusKm}
            disabled={busy}
            inputMode="numeric"
            placeholder="60 km"
            onChange={(e) => setRadiusKm(e.target.value.replace(/[^\d]/g, ''))}
            data-testid="search-radius"
            style={{ width: '100%', marginTop: 8, padding: '9px 10px', border: '1px solid var(--color-divider)', font: 'inherit' }}
          />
        </div>

        <div style={{ minWidth: 0 }}>
          <p className="flbl" style={{ margin: '0 0 8px' }}>
            Shoot window
          </p>
          <input
            type="date"
            value={from}
            disabled={busy}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="shoot-window-start"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--color-divider)', font: 'inherit' }}
          />
          <input
            type="date"
            value={to}
            disabled={busy}
            onChange={(e) => setTo(e.target.value)}
            data-testid="shoot-window-end"
            style={{ width: '100%', marginTop: 8, padding: '8px 10px', border: '1px solid var(--color-divider)', font: 'inherit' }}
          />
        </div>
      </div>

      {/* COLLAPSED once a country is chosen: the list is the way IN to a choice,
          and a producer who has chosen does not need eight rows re-read. */}
      {recommended.length === 0 ? null : (
        <div
          data-testid="recommended-countries"
          style={{
            marginTop: 18,
            border: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
            padding: '4px 14px 10px',
          }}
        >
          <Disclosure
            summary={`Recommended for this script (${String(recommended.length)})`}
            defaultOpen={current.countries.length === 0}
            testId="recommended-countries-toggle"
          >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <p className="mm" style={{ margin: 0, fontSize: 12 }}>
              Where we hold the most locations, then the incentive.
            </p>
            <button
              type="button"
              className="ctl"
              data-testid="add-all-recommended"
              disabled={busy}
              onClick={() =>
                setCountries((c) => [...new Set([...c, ...recommended.map((r) => r.iso)])])
              }
              style={{ marginLeft: 'auto' }}
            >
              Add all
            </button>
          </div>

          <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: 8 }}>
            {recommended.map((r) => {
              const added = countries.includes(r.iso);
              return (
                <li
                  key={r.iso}
                  style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}
                >
                  <strong style={{ fontSize: 13, minWidth: 90 }}>{r.name}</strong>
                  {/* Density first, incentive second: a 40% rebate where we hold
                      four listings is not a recommendation, and leading with the
                      rate would make it look like one. */}
                  <span className="mm" style={{ fontSize: 12, flex: 1, minWidth: 200 }}>
                    {r.reason}
                  </span>
                  <button
                    type="button"
                    className="ctl"
                    data-testid={`add-recommended-${r.iso}`}
                    data-on={added ? '1' : '0'}
                    disabled={busy || added}
                    onClick={() => toggle(r.iso)}
                  >
                    {added ? 'Added' : 'Add'}
                  </button>
                </li>
              );
            })}
          </ul>

          <p className="mm" style={{ fontSize: 11, margin: '10px 0 0' }}>
            {disclaimer}
          </p>
          </Disclosure>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 18,
          marginTop: 18,
        }}
      >
        <label style={{ display: 'block' }}>
          <span className="flbl">Location budget</span>
          <select
            value={budgetBand}
            disabled={busy}
            onChange={(e) => setBudgetBand(e.target.value)}
            data-testid="budget-band"
            style={{ width: '100%', marginTop: 6, padding: '9px 10px', border: '1px solid var(--color-divider)', font: 'inherit' }}
          >
            <option value="">No figure yet</option>
            {BUDGET_BANDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>

        <Choice
          label="Studio vs practical"
          testId="studio-preference"
          value={studio}
          disabled={busy}
          onChange={setStudio}
          options={[
            ['STUDIO', 'Studio'],
            ['PRACTICAL', 'Practical'],
            ['BOTH', 'Both'],
          ]}
        />

        <Choice
          label="Interior / exterior"
          testId="interior-exterior"
          value={intExt}
          disabled={busy}
          onChange={setIntExt}
          options={[
            ['INTERIOR', 'Interior'],
            ['EXTERIOR', 'Exterior'],
            ['BOTH', 'Both'],
          ]}
        />

        <div>
          <span className="flbl">AI digital twin</span>
          {/* A REAL FILTER, over `digital_twin_status = 'available'`. It is
              honest about what it will do: no twins have been built yet, so
              choosing it today narrows to nothing — and the results screen says
              THAT rather than showing an empty list, which would read as "we
              have nothing like this" instead of "nothing has a twin yet". */}
          <div role="group" aria-label="AI digital twin" style={{ display: 'flex', gap: 0, marginTop: 6 }}>
            <button
              type="button"
              className="sub"
              data-testid="twin-any"
              data-on={twinRequired ? '0' : '1'}
              aria-pressed={!twinRequired}
              disabled={busy}
              onClick={() => setTwinRequired(false)}
              style={{ flex: 1 }}
            >
              Any
            </button>
            <button
              type="button"
              className="sub"
              data-testid="twin-required"
              data-on={twinRequired ? '1' : '0'}
              aria-pressed={twinRequired}
              disabled={busy}
              onClick={() => setTwinRequired(true)}
              style={{ flex: 1 }}
            >
              Twin available
            </button>
          </div>
          {twinRequired ? (
            <p className="mm" data-testid="twin-warning" style={{ fontSize: 11, margin: '6px 0 0' }}>
              No twins have been built yet, so this will narrow the search to nothing for now.
            </p>
          ) : null}
        </div>
      </div>

      <label style={{ display: 'block', marginTop: 18 }}>
        <span className="flbl">Anything else the script doesn’t say</span>
        <textarea
          value={note}
          disabled={busy}
          rows={2}
          maxLength={4000}
          placeholder="Crew size, night access, a location the director already has in mind…"
          onChange={(e) => setNote(e.target.value)}
          data-testid="constraints-note"
          style={{ width: '100%', marginTop: 6, padding: '9px 10px', border: '1px solid var(--color-divider)', font: 'inherit' }}
        />
      </label>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 18 }}>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="search-locations"
          disabled={busy}
          onClick={() => onSearch(draft())}
        >
          {busy ? 'Searching…' : 'Search locations'}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          data-testid="skip-search-parameters"
          disabled={busy}
          onClick={onSkip}
        >
          Skip — use script requirements only
        </button>
        {onCancel === undefined ? null : (
          <button type="button" className="ctl" data-testid="search-parameters-cancel" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </section>
  );
}

function Choice({
  label,
  testId,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  testId: string;
  value: string;
  options: readonly (readonly [string, string])[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <span className="flbl">{label}</span>
      <div role="group" aria-label={label} style={{ display: 'flex', gap: 0, marginTop: 6 }}>
        {options.map(([v, l]) => (
          <button
            key={v}
            type="button"
            className="sub"
            data-testid={`${testId}-${v}`}
            data-on={value === v ? '1' : '0'}
            aria-pressed={value === v}
            disabled={disabled}
            onClick={() => onChange(v)}
            style={{ flex: 1 }}
          >
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

/** "12 Jan 2027" — the day a person reads, never the ISO string the API sends. */
function readableDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * THE ACTIVE SEARCH IN ONE ROW (2026-10-30).
 *
 * What the producer told us — where, when, on what — said back as short facts,
 * with the full form one click away instead of always open above the results.
 * Only what was actually set is listed: "Both" for studio or interior is no
 * constraint and is left out, and a production that skipped the question reads
 * "Script requirements only" rather than a row of empty values.
 */
export function SearchCriteriaBar({
  current,
  catalogue,
  editing,
  onEdit,
}: {
  current: SearchParametersState;
  catalogue: readonly CountryIncentiveRow[];
  editing: boolean;
  onEdit: () => void;
}) {
  const names = new Map(catalogue.map((c) => [c.iso, c.name]));
  const countries = current.countries.map((iso) => names.get(iso) ?? iso);

  const items: { id: string; label: string; value: string; title?: string }[] = [];
  if (countries.length > 0) {
    items.push({
      id: 'countries',
      label: countries.length === 1 ? 'Country' : 'Countries',
      value: countries.length <= 3 ? countries.join(', ') : `${countries.slice(0, 3).join(', ')} +${String(countries.length - 3)}`,
      title: countries.join(', '),
    });
  }
  if (current.city !== null) {
    items.push({
      id: 'area',
      label: 'Area',
      value: current.radiusKm === null ? current.city : `${current.city} · ${String(current.radiusKm)} km`,
    });
  }
  if (current.shootWindowStart !== null || current.shootWindowEnd !== null) {
    items.push({
      id: 'dates',
      label: 'Dates',
      value:
        current.shootWindowStart !== null && current.shootWindowEnd !== null
          ? `${readableDay(current.shootWindowStart)} – ${readableDay(current.shootWindowEnd)}`
          : current.shootWindowStart !== null
            ? `From ${readableDay(current.shootWindowStart)}`
            : `Until ${readableDay(current.shootWindowEnd ?? '')}`,
    });
  }
  if (current.budgetBand !== null) items.push({ id: 'budget', label: 'Budget', value: current.budgetBand });
  if (current.studioPreference === 'STUDIO' || current.studioPreference === 'PRACTICAL') {
    items.push({ id: 'studio', label: 'Type', value: current.studioPreference === 'STUDIO' ? 'Studio' : 'Practical' });
  }
  if (current.interiorExterior === 'INTERIOR' || current.interiorExterior === 'EXTERIOR') {
    items.push({ id: 'int-ext', label: 'Setting', value: current.interiorExterior === 'INTERIOR' ? 'Interior' : 'Exterior' });
  }
  if (current.twinRequired) items.push({ id: 'twin', label: 'Twin', value: 'AI digital twin available' });
  if (current.constraintsNote !== null) {
    items.push({
      id: 'note',
      label: 'Note',
      value: current.constraintsNote.length > 48 ? `${current.constraintsNote.slice(0, 48)}…` : current.constraintsNote,
      title: current.constraintsNote,
    });
  }

  return (
    <div className="loc-criteria" data-testid="search-criteria">
      <span className="loc-criteria-label">Search criteria</span>
      {items.length > 0 ? (
        <ul className="loc-criteria-list">
          {items.map((item) => (
            <li key={item.id} className="loc-criterion" data-testid={`criterion-${item.id}`} title={item.title}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </li>
          ))}
        </ul>
      ) : (
        <p className="loc-criteria-none" data-testid="criteria-none" style={{ margin: 0, flex: '1 1 240px' }}>
          {current.setAt === null ? 'Not set yet — tell us where, when and on what budget.' : 'Script requirements only — no place, date or budget set.'}
        </p>
      )}
      <div className="loc-criteria-actions">
        <button
          type="button"
          className="ctl"
          data-testid="edit-criteria"
          aria-expanded={editing}
          onClick={onEdit}
        >
          {editing ? 'Close criteria' : 'Edit criteria'}
        </button>
      </div>
    </div>
  );
}
